import { createAsyncThunk } from '@reduxjs/toolkit'

import { NpPatientBeingBookedFor } from '@lyrahealth-inc/shared-app-logic/src/features/npTesting/types'
import {
  NpAvailableTimesRequest,
  NpAvailableTimesResponse,
  NpBookableSessionType,
  NpScheduleSessionsRequest,
  NpScheduleSessionsResponse,
  NpSelectedAppointment,
  NpSessionsResponse,
} from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import {
  getNpSessions as fetchNpSessions,
  postNpAvailableTimes,
  postNpScheduleSessions,
} from '../../../common/http/data/npTesting'

export const GET_NP_AVAILABLE_TIMES = 'GET_NP_AVAILABLE_TIMES'

export const getNpAvailableTimes = createAsyncThunk<NpAvailableTimesResponse, NpAvailableTimesRequest>(
  GET_NP_AVAILABLE_TIMES,
  async (request, { rejectWithValue }) => {
    try {
      const response = await postNpAvailableTimes(request)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const CLEAR_NP_AVAILABLE_TIMES = 'CLEAR_NP_AVAILABLE_TIMES'
export const clearNpAvailableTimes = () => ({ type: CLEAR_NP_AVAILABLE_TIMES })

export const GET_NP_AVAILABLE_TIMES_FULFILLED = getNpAvailableTimes.fulfilled.type
export const GET_NP_AVAILABLE_TIMES_PENDING = getNpAvailableTimes.pending.type
export const GET_NP_AVAILABLE_TIMES_REJECTED = getNpAvailableTimes.rejected.type

export const SET_NP_SELECTED_PROVIDER = 'SET_NP_SELECTED_PROVIDER'
export const setNpSelectedProvider = (provider: ProviderInfo) => ({ type: SET_NP_SELECTED_PROVIDER, payload: provider })

export const CLEAR_NP_SELECTED_PROVIDER = 'CLEAR_NP_SELECTED_PROVIDER'
export const clearNpSelectedProvider = () => ({ type: CLEAR_NP_SELECTED_PROVIDER })

/** Which NP appointment the calendar and booking pages are currently working on */
export const SET_NP_CURRENT_SESSION_TYPE = 'SET_NP_CURRENT_SESSION_TYPE'
export const setNpCurrentSessionType = (sessionType: NpBookableSessionType) => ({
  type: SET_NP_CURRENT_SESSION_TYPE,
  payload: sessionType,
})

/** Stores the time slot picked on the calendar, keyed by session type, until the booking request is sent */
export const SET_NP_SELECTED_APPOINTMENT = 'SET_NP_SELECTED_APPOINTMENT'
export const setNpSelectedAppointment = (appointment: NpSelectedAppointment) => ({
  type: SET_NP_SELECTED_APPOINTMENT,
  payload: appointment,
})

export const CLEAR_NP_SELECTED_APPOINTMENTS = 'CLEAR_NP_SELECTED_APPOINTMENTS'
export const clearNpSelectedAppointments = () => ({ type: CLEAR_NP_SELECTED_APPOINTMENTS })

/** Who the flow is booking for. Set when the overview page lands, cleared with the rest of the flow. */
export const SET_NP_PATIENT_BEING_BOOKED_FOR = 'SET_NP_PATIENT_BEING_BOOKED_FOR'
export const setNpPatientBeingBookedFor = (patient: NpPatientBeingBookedFor) => ({
  type: SET_NP_PATIENT_BEING_BOOKED_FOR,
  payload: patient,
})

/** Resets everything from a booking flow. Dispatched when a flow starts and when it completes. */
export const CLEAR_NP_BOOKING_FLOW = 'CLEAR_NP_BOOKING_FLOW'
export const clearNpBookingFlow = () => ({ type: CLEAR_NP_BOOKING_FLOW })

export const SCHEDULE_NP_SESSIONS = 'SCHEDULE_NP_SESSIONS'

export const scheduleNpSessions = createAsyncThunk<
  NpScheduleSessionsResponse,
  { userLyraId: string; request: NpScheduleSessionsRequest }
>(SCHEDULE_NP_SESSIONS, async ({ userLyraId, request }, { rejectWithValue }) => {
  try {
    const response = await postNpScheduleSessions(userLyraId, request)
    return response.data
  } catch (error) {
    return rejectWithValue(error)
  }
})

export const CLEAR_NP_SCHEDULED_SESSIONS = 'CLEAR_NP_SCHEDULED_SESSIONS'
export const clearNpScheduledSessions = () => ({ type: CLEAR_NP_SCHEDULED_SESSIONS })

export const SCHEDULE_NP_SESSIONS_FULFILLED = scheduleNpSessions.fulfilled.type
export const SCHEDULE_NP_SESSIONS_PENDING = scheduleNpSessions.pending.type
export const SCHEDULE_NP_SESSIONS_REJECTED = scheduleNpSessions.rejected.type

export const GET_NP_SESSIONS = 'GET_NP_SESSIONS'

/**
 * Loads the member's NP episode so a member arriving part way through (e.g. after cancelling, or coming back to book
 * the testing sessions) resumes from the booked sessions. `userLyraId` is the patient's id.
 */
export const getNpSessions = createAsyncThunk<NpSessionsResponse, string>(
  GET_NP_SESSIONS,
  async (userLyraId, { rejectWithValue }) => {
    try {
      const response = await fetchNpSessions(userLyraId)
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const CLEAR_NP_SESSIONS_LOADING_STATUS = 'CLEAR_NP_SESSIONS_LOADING_STATUS'
export const clearNpSessionsLoadingStatus = () => ({ type: CLEAR_NP_SESSIONS_LOADING_STATUS })

export const GET_NP_SESSIONS_FULFILLED = getNpSessions.fulfilled.type
export const GET_NP_SESSIONS_PENDING = getNpSessions.pending.type
export const GET_NP_SESSIONS_REJECTED = getNpSessions.rejected.type
